import {DataTable} from "../DataTable/DataTable";
import {CompanyEntity} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {Box, Modal, ModalProps} from "@mantine/core";
import {usePagination} from "../DataTable/usePagination";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {useCompanyDataTableFilter} from "../CompanyDataTable/useCompanyDataTableFilter";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {MouseEvent, useCallback, useMemo} from "react";
import {DataTableColumn} from "mantine-datatable";
import {sortBy} from "lodash";
import {useNavigate} from "react-router-dom";
import {EntityActionGroup} from "../../group/EntityActionGroup/EntityActionGroup";
import {EntityAnchor} from "../../anchor/EntityAnchor/EntityAnchor";
import { FormattedDatabaseCode } from "../../text/FormattedDatabaseCode/FormattedDatabaseCode";

const EMPTY_ARRAY: CompanyEntity[] = []

export type CompanyDataTableProps = EntityDataTableProps<CompanyEntity>

export const CompanyDataTable = (props: CompanyDataTableProps) => {
  const {
    records: roles = EMPTY_ARRAY,
    recordsPerPageOptions,
    ...rest
  } = props

  const {t} = useTranslation(["common"]);
  const navigate = useNavigate()

  const {filteredRecords, filterConfig} = useCompanyDataTableFilter({
    records: roles
  })

  const sortConfig = useMemo(() => {
    return {
      "name": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.name?.toLowerCase()
          }])
        }
      },
    }
  }, [])

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'name', direction: 'asc'},
    sortConfig: sortConfig,
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const onCompanyActionClick = useCallback((record: CompanyEntity, action: string) => {
    // noop
  }, [])

  const onViewActionClick = useCallback((e: MouseEvent<HTMLButtonElement>, record: CompanyEntity) => {
    e.stopPropagation()
    navigate(`/companies/profile?id=${record.id}`)
  }, [navigate])

  const onEditActionClick = useCallback((e: MouseEvent<HTMLButtonElement>, record: CompanyEntity) => {
    e.stopPropagation()
    onCompanyActionClick(record, 'EDIT')
  }, [onCompanyActionClick])

  const onDeleteActionClick = useCallback((e: MouseEvent<HTMLButtonElement>, record: CompanyEntity) => {
    e.stopPropagation()
    onCompanyActionClick(record, 'DELETE')
  }, [onCompanyActionClick])

  const columns = useMemo(() => {
    const values: DataTableColumn<CompanyEntity>[] = [
      {
        ...getColumnFilterProps(filterConfig, "name"),
        title: t("common.entity.company.table.column.name", "Name"),
        accessor: 'name',
        width: 400,
        sortable: true,
        render: (record) => (
          <EntityAnchor value={record}/>
        ),
      },
      {
        ...getColumnFilterProps(filterConfig, "type"),
        title: t("common.entity.company.table.column.type", "Type"),
        accessor: 'type',
        width: 400,
        sortable: true,
        render: (record) => (
          <FormattedDatabaseCode value={record.type} />
        ),
      },
      {
        title: '',
        accessor: "actions",
        width: 100,
        textAlignment: 'right',
        render: (company) => (
          <EntityActionGroup
            permissionPrefix={"company"}
            edit={false}
            delete={false}
            onViewActionClick={(e) => onViewActionClick(e, company)}
            onEditActionClick={(e) => onEditActionClick(e, company)}
            onDeleteActionClick={(e) => onDeleteActionClick(e, company)}
          />
        )
      }
    ]

    return values
  }, [filterConfig, onEditActionClick, onViewActionClick, onDeleteActionClick, t])

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={columns}
    />
  )
}

export type CompanyDataTableModalProps = Omit<ModalProps, "onSubmit"> & {
  dataTable: Omit<CompanyDataTableProps, 'recordsPerPageOptions'>
}

export const CompanyDataTableModal = (props: CompanyDataTableModalProps) => {
  const {t} = useTranslation(["common"]);

  const {
    title = t<string>("common.entity.company.plural", "Companies"),
    dataTable,
    ...rest
  } = props;

  return (
    <Modal {...rest} title={title}>
      <Box>
        <CompanyDataTable
          {...dataTable}
          recordsPerPageOptions={[5]}/>
      </Box>
    </Modal>
  );
};
